/**
 * TsTimePicker_standAlone - Vanilla JavaScript time picker constructor
 * Mimics the design and function of the Ant Design Vue TimePicker component
 * for use on pages that do not use Vue.
 *
 * Usage:
 *   const picker = new TsTimePicker_standAlone({
 *     container: document.getElementById('time-picker-mount'),
 *     value: '09:00',
 *     use12Hours: true,
 *     onChange: (value) => console.log(value) // "HH:mm"
 *   });
 *   picker.setValue('17:30');
 *   const value = picker.getValue();
 *   picker.destroy();
 */

var TsTimePickerExport = (function (global) {
  'use strict';

  const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  /** Minute dropdown: fixed list [00,10,…,50], not scrollable. */
  const MINUTES_10 = [0, 10, 20, 30, 40, 50];

  /**
   * Parse "HH:mm" (24h) to { hours24, hours12, minutes, isPM }
   * @param {string|Date} value
   * @param {number} minuteStep - 1 = all 60 minutes, 5 = 5-minute steps
   */
  function parseTime(value, minuteStep) {
    var snapToStep = minuteStep === 5;
    if (!value) return { hours24: 9, hours12: 9, minutes: 0, isPM: false };
    if (value instanceof Date) {
      var minutes = value.getMinutes();
      if (snapToStep) {
        minutes = Math.round(minutes / 5) * 5;
        if (minutes === 60) minutes = 55;
      } else {
        minutes = Math.min(59, minutes);
      }
      return {
        hours24: value.getHours(),
        hours12: value.getHours() % 12 || 12,
        minutes: minutes,
        isPM: value.getHours() >= 12
      };
    }
    var str = String(value).trim();
    var match = str.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return { hours24: 9, hours12: 9, minutes: 0, isPM: false };
    var hours24 = Math.min(23, Math.max(0, parseInt(match[1], 10)));
    var minutes = Math.min(59, Math.max(0, parseInt(match[2], 10)));
    if (snapToStep) {
      minutes = Math.round(minutes / 5) * 5;
      if (minutes === 60) minutes = 55;
    }
    var hours12 = hours24 % 12 || 12;
    return { hours24: hours24, hours12: hours12, minutes: minutes, isPM: hours24 >= 12 };
  }

  /**
   * Format { hours24, minutes } to "HH:mm"
   */
  function toHHmm(hours24, minutes) {
    const h = String(hours24).padStart(2, '0');
    const m = String(minutes).padStart(2, '0');
    return h + ':' + m;
  }

  /**
   * Format for display: "h:mm A" (12-hour)
   */
  function format12h(hours24, minutes) {
    const hours12 = hours24 % 12 || 12;
    const m = String(minutes).padStart(2, '0');
    const ampm = hours24 >= 12 ? 'PM' : 'AM';
    return hours12 + ':' + m + ' ' + ampm;
  }

  /**
   * Keep only allowed typed characters for the input: 0-9, :, A/P/M (auto-uppercase).
   */
  function sanitizeTypedTimeInput(raw) {
    var out = '';
    var src = String(raw == null ? '' : raw);
    for (var i = 0; i < src.length; i++) {
      var ch = src.charAt(i);
      if ((ch >= '0' && ch <= '9') || ch === ':') {
        out += ch;
      } else if (ch === 'a' || ch === 'A' || ch === 'p' || ch === 'P' || ch === 'm' || ch === 'M') {
        out += ch.toUpperCase();
      }
    }
    return out;
  }

  /**
   * Parse typed input into picker state.
   * Supports 12h (e.g. 9:30AM, 9:30A, 9:30) and 24h (e.g. 21:30, 9:30).
   */
  function parseTypedTimeInput(value, use12Hours, minuteStep, currentState) {
    var cleaned = sanitizeTypedTimeInput(value).replace(/\s+/g, '');
    if (!cleaned) return null;

    var h;
    var m;
    var meridian;
    var match;

    if (use12Hours) {
      match = cleaned.match(/^(\d{1,2})(?::?(\d{1,2}))?([AP]M?)?$/);
      if (!match) return null;
      h = parseInt(match[1], 10);
      m = match[2] === undefined || match[2] === '' ? 0 : parseInt(match[2], 10);
      meridian = (match[3] || '').toUpperCase();
      if (meridian === 'A') meridian = 'AM';
      if (meridian === 'P') meridian = 'PM';
      if (h < 1 || h > 12 || isNaN(m) || m < 0 || m > 59) return null;

      if (minuteStep === 5) {
        m = Math.round(m / 5) * 5;
        if (m === 60) m = 55;
      }

      var isPM = meridian ? meridian === 'PM' : !!(currentState && currentState.isPM);
      var hours24 = isPM ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);
      return {
        hours24: hours24,
        hours12: h,
        minutes: m,
        isPM: isPM
      };
    }

    match = cleaned.match(/^(\d{1,2})(?::?(\d{1,2}))?$/);
    if (!match) return null;
    h = parseInt(match[1], 10);
    m = match[2] === undefined || match[2] === '' ? 0 : parseInt(match[2], 10);
    if (isNaN(h) || h < 0 || h > 23 || isNaN(m) || m < 0 || m > 59) return null;

    if (minuteStep === 5) {
      m = Math.round(m / 5) * 5;
      if (m === 60) m = 55;
    }

    return {
      hours24: h,
      hours12: h % 12 || 12,
      minutes: m,
      isPM: h >= 12
    };
  }

  /**
   * Inject default styles so picker works without Ant Design CSS
   */
  function injectStyles() {
    const panelFontOverride = `
      .ant-time-picker-panel-combobox { font-size: 18px !important; }
      .ant-time-picker-panel-select li { font-size: 18px !important; }
    `;
    const existing = document.getElementById('ts-time-picker-styles');
    if (existing) {
      if (existing.textContent.indexOf('.ant-time-picker-panel-combobox { font-size: 18px !important; }') === -1) {
        existing.textContent += panelFontOverride;
      }
      const minuteNoScroll = '.ant-time-picker-panel-select[data-type="minute"] { max-height: none; overflow-y: visible; }';
      if (existing.textContent.indexOf('[data-type="minute"]') === -1) {
        existing.textContent += minuteNoScroll;
      }
      return;
    }
    const css = `
      .ts-time-picker-standalone.ant-time-picker { width: 150px; }
      .ts-time-picker-standalone .ant-time-picker-input { font-size: 18px; height: auto; min-height: 35px; padding: 2px 2px 2px 10px; font-weight:bold; border-radius: 3px; width: 131px; max-width: 131px; font-family: inherit; color: #000; border: 1px solid #d9d9d9; }
      .ts-time-picker-standalone .ant-time-picker-input:focus { border-color: #1890ff; outline: 0; }
      .ts-time-picker-standalone .ant-time-picker-input:disabled { color: rgba(0,0,0,.25); background: #f5f5f5; cursor: not-allowed; }
      .ant-time-picker-panel { z-index: 999; min-width: 188px; }
      .ant-time-picker-panel-inner { background: #fff; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,.15); padding: 4px 0;}
      .ant-time-picker-panel-combobox { display: flex; font-size: 18px; }
      .ant-time-picker-panel-select { flex: 1; max-height: 200px; overflow-y: auto; border: none; scrollbar-width: none; -ms-overflow-style: none; }
      .ant-time-picker-panel-select[data-type="minute"] { max-height: none; overflow-y: visible; }
      .ant-time-picker-panel-select:not([data-is-ampm="true"]) { margin-top: 4px; border-right: 1px solid #f0f0f0; }
      .ant-time-picker-panel-select::-webkit-scrollbar { display: none; }
      .ant-time-picker-panel-select ul { list-style: none; margin: 0; padding: 4px 0; }
      .ant-time-picker-panel-select li { padding: 4px 12px; cursor: pointer; text-align: center; font-size: 18px; }
      .ant-time-picker-panel-select li.ant-time-picker-panel-cell-selected { color: inherit; font-weight: inherit; }
      .ant-time-picker-panel-select[data-is-ampm="true"] { max-height: none; }
      .ant-time-picker-panel-select[data-is-ampm="true"] ul { padding-top: 4px; }
      .ant-time-picker-panel-select[data-is-ampm="true"] li { height: 28px !important; box-sizing: border-box; display: flex; align-items: center; justify-content: center; }
      .ant-time-picker-panel-addon { padding: 8px 12px; border-top: 1px solid #f0f0f0; text-align: left; }
      .ant-time-picker-panel-addon { padding: 8px 12px; border-top: 1px solid #f0f0f0; text-align: left; display: flex; align-items: center; flex-wrap: wrap; justify-content: space-between; font-weight: normal; }
      .ant-time-picker-panel-addon .ts-time-picker-now { color: #1890ff; cursor: pointer; margin-right: 8px; }
      .ant-time-picker-panel-addon .ts-time-picker-ok { color: #1890ff; cursor: pointer; }
      .ant-time-picker-panel-addon .ts-time-picker-minute-toggle { color: #000; }
      .ant-time-picker-panel-addon .ts-time-picker-minute-toggle > span[data-step] {
        display: inline-block;
        padding: 2px 10px;
        border-radius: 9999px;
        line-height: 1.35;
        vertical-align: middle;
        font-size: inherit;
        box-sizing: border-box;
      }
      .ant-time-picker-panel-addon .ts-time-picker-minute-toggle > span[data-step]:not(.ts-time-picker-minute-link) {
        background: #1890ff;
        color: #fff;
        cursor: default;
        font-weight: bold;
      }
      .ant-time-picker-panel-addon .ts-time-picker-minute-toggle .ts-time-picker-minute-link {
        background: transparent;
        color: #1890ff;
        cursor: pointer;
      }
      .ant-time-picker-panel-addon .ts-time-picker-minute-toggle .ts-time-picker-minute-link:hover { text-decoration: underline; }
      .ant-time-picker-input-wrap-v2 {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: 2px;
        background-color: white;
        padding: 0px 0px 0px 0px;
        box-sizing: border-box;
        border: 1px solid #b6b6b6;
      }
    `;
    const style = document.createElement('style');
    style.id = 'ts-time-picker-styles';
    style.textContent = css + panelFontOverride;
    document.head.appendChild(style);
  }

  /**
   * TsTimePicker_standAlone constructor
   * @param {Object} options
   * @param {HTMLElement} options.container - Element to mount the picker into
   * @param {string|Date} [options.value] - Initial time "HH:mm" or Date
   * @param {boolean} [options.use12Hours=true] - Use 12-hour format
   * @param {string} [options.format='h:mm A'] - Display format (only h:mm A supported for 12h)
   * @param {boolean} [options.disabled=false]
   * @param {boolean} [options.allowClear=false]
   * @param {boolean} [options.showNowButton=false] - Show "Now" link in footer
   * @param {boolean} [options.showOkayButton=true] - Show "Okay" button in footer
   * @param {Function} [options.onChange] - Callback(value) where value is "HH:mm"
   * @param {Function} [options.getPopupContainer] - Return element for dropdown (default: document.body)
   * @param {string} [options.placeholder='Select time']
   * @param {string} [options.name] - Input name attribute
   * @param {string} [options.className] - Extra class on wrapper
   * @param {number} [options.minuteStep=1] - 1 = show all 60 minutes (default), 5 = show 5-minute steps (00, 05, ..., 55)
   * @param {boolean} [options.lockPageScroll=true] - When true, lock host page scroll while panel is open
   * @param {Function} [options.onOpen] - Callback when panel opens
   * @param {Function} [options.onClose] - Callback when panel closes
   */
  function TsTimePicker_standAlone(options) {
    options = options || {};
    this.version = "stand_alone_TimePickerV2_v1.1.5";
    this.container = options.container;
    this.use12Hours = options.use12Hours !== false;
    this.format = options.format || 'h:mm A';
    this.minuteStep = options.minuteStep !== undefined ? options.minuteStep : 1;
    this.disabled = !!options.disabled;
    this.allowClear = !!options.allowClear;
    this.showNowButton = !!options.showNowButton;
    this.showOkayButton = options.showOkayButton !== false;
    this.onChange = typeof options.onChange === 'function' ? options.onChange : function () {};
    this.onOpen = typeof options.onOpen === 'function' ? options.onOpen : function () {};
    this.onClose = typeof options.onClose === 'function' ? options.onClose : function () {};
    this.getPopupContainer = typeof options.getPopupContainer === 'function'
      ? options.getPopupContainer
      : function () { return document.body; };
    this.placeholder = options.placeholder != null ? options.placeholder : 'Select time';
    this.name = options.name || '';
    this.className = options.className || '';
    this.lockPageScroll = options.lockPageScroll !== false;

    this._state = parseTime(options.value, this.minuteStep);
    this._wrapper = null;
    this._input = null;
    this._panel = null;
    this._boundClose = this._handleClickOutside.bind(this);
    this._id = 'ts-time-picker-' + Math.random().toString(36).slice(2, 11);

    injectStyles();
    this._render();
  }

  TsTimePicker_standAlone.prototype._render = function () {
    const wrapper = document.createElement('div');
    wrapper.className = 'ant-time-picker ts-time-picker-standalone ' + this.className;
    wrapper.setAttribute('data-ts-time-picker', this._id);

    const inputWrap = document.createElement('div');
    inputWrap.className = 'ant-time-picker-input-wrap-v2';

    const input = document.createElement('input');
    input.className = 'ant-time-picker-input';
    input.type = 'text';
    input.readOnly = false;
    input.placeholder = this.placeholder;
    if (this.name) input.name = this.name;
    if (this.disabled) input.disabled = true;
    input.value = this.use12Hours
      ? format12h(this._state.hours24, this._state.minutes)
      : toHHmm(this._state.hours24, this._state.minutes);

    inputWrap.appendChild(input);
    wrapper.appendChild(inputWrap);
    this._wrapper = wrapper;
    this._input = input;

    wrapper.addEventListener('mousedown', function (e) {
      if (e.target === this._input && (!this._panel || !this._panel.parentNode)) {
        e.preventDefault();
        if (this.lockPageScroll) this._lockPageScroll();
      }
    }.bind(this), true);
    input.addEventListener('click', this._openPanel.bind(this));
    input.addEventListener('focus', this._openPanel.bind(this));
    input.addEventListener('input', this._handleTypedInput.bind(this));
    input.addEventListener('keydown', this._handleTypedKeyDown.bind(this));
    input.addEventListener('blur', this._commitTypedInput.bind(this));

    if (this.container) {
      this.container.appendChild(wrapper);
    }
  };

  TsTimePicker_standAlone.prototype._openPanel = function () {
    console.log('TsTimePicker_standAlone._openPanel called');
    if (this.disabled) return;
    if (this._panel && this._panel.parentNode) {
      this._closePanel();
      return;
    }
    if (this.lockPageScroll) this._lockPageScroll();
    this._panel = this._createPanel();
    var popupRoot = this.getPopupContainer();
    popupRoot.appendChild(this._panel);
    this._positionPanel();
    document.addEventListener('mousedown', this._boundClose);
    this._scrollToSelected();
    this._attachHoursScrollLog();
    this.onOpen();
  };

  TsTimePicker_standAlone.prototype._closePanel = function () {
    if (this._panel && this._panel.parentNode) {
      this.onClose();
    }
    if (this._hoursScrollLogCleanup) {
      this._hoursScrollLogCleanup();
    }
    if (this._panel && this._panel.parentNode) {
      this._panel.parentNode.removeChild(this._panel);
      this._panel = null;
    }
    document.removeEventListener('mousedown', this._boundClose);
    if (this.lockPageScroll) this._unlockPageScroll();
  };

  TsTimePicker_standAlone.prototype._lockPageScroll = function () {
    if (this._scrollLocked) return;
    this._scrollLocked = true;
    const docEl = document.documentElement;
    const scrollbarWidth = window.innerWidth - docEl.clientWidth;
    this._prevScrollLock = {
      htmlOverflow: docEl.style.overflow,
      bodyPaddingRight: document.body.style.paddingRight
    };
    docEl.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = scrollbarWidth + 'px';
    }
  };

  TsTimePicker_standAlone.prototype._unlockPageScroll = function () {
    if (!this._scrollLocked) return;
    this._scrollLocked = false;
    const docEl = document.documentElement;
    if (this._prevScrollLock) {
      docEl.style.overflow = this._prevScrollLock.htmlOverflow !== undefined ? this._prevScrollLock.htmlOverflow : '';
      document.body.style.paddingRight = this._prevScrollLock.bodyPaddingRight !== undefined ? this._prevScrollLock.bodyPaddingRight : '';
    }
    this._prevScrollLock = null;
  };


  TsTimePicker_standAlone.prototype._handleClickOutside = function (e) {
    if (!this._panel) return;
    var w = this._wrapper;
    var p = this._panel;
    if (w && w.contains(e.target)) return;
    if (p && p.contains(e.target)) return;
    this.onChange(this.getValue());
    this._closePanel();
  };

  /**
   * Position the dropdown panel below (or above) the input.
   * Uses fixed positioning when appended to body, absolute when inside a custom getPopupContainer.
   */
  TsTimePicker_standAlone.prototype._positionPanel = function () {
    if (!this._input || !this._panel) return;
    const rect = this._input.getBoundingClientRect();
    const gap = 0;
    const popupRoot = this.getPopupContainer();
    const isBody = popupRoot === document.body;
    this._panel.style.width = rect.width + 'px';
    const panelHeight = this._panel.offsetHeight;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = panelHeight > 0 && spaceBelow < panelHeight + gap;
    if (isBody) {
      // Fixed: position relative to viewport
      this._panel.style.position = 'fixed';
      this._panel.style.left = rect.left + 'px';
      this._panel.style.top = openAbove
        ? (rect.top - panelHeight - gap) + 'px'
        : (rect.bottom + gap) + 'px';
    } else {
      // Absolute: position relative to popupRoot
      const rootRect = popupRoot.getBoundingClientRect();
      this._panel.style.position = 'absolute';
      this._panel.style.left = (rect.left - rootRect.left) + 'px';
      this._panel.style.top = openAbove
        ? (rect.top - rootRect.top - panelHeight - gap) + 'px'
        : (rect.bottom - rootRect.top + gap) + 'px';
    }
  };

  TsTimePicker_standAlone.prototype._createPanel = function () {
    const panel = document.createElement('div');
    panel.className = 'ant-time-picker-panel';
    panel.setAttribute('data-ts-time-picker-panel', this._id);

    const inner = document.createElement('div');
    inner.className = 'ant-time-picker-panel-inner';

    const combobox = document.createElement('div');
    combobox.className = 'ant-time-picker-panel-combobox';

    if (this.use12Hours) {
      const hourSelect = this._createSelect('hour', HOURS_12, this._state.hours12, function (val) {
        const h = parseInt(val, 10);
        const hours24 = this._state.isPM ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);
        this._state.hours24 = hours24;
        this._state.hours12 = h;
        this._syncInput();
      }.bind(this));
      combobox.appendChild(hourSelect);
    } else {
      const hours24 = Array.from({ length: 24 }, (_, i) => i);
      const hourSelect = this._createSelect('hour', hours24, this._state.hours24, function (val) {
        this._state.hours24 = parseInt(val, 10);
        this._state.hours12 = this._state.hours24 % 12 || 12;
        this._state.isPM = this._state.hours24 >= 12;
        this._syncInput();
      }.bind(this));
      combobox.appendChild(hourSelect);
    }

    const minuteSelect = this._createSelect('minute', MINUTES_10, this._state.minutes, function (val) {
      this._state.minutes = parseInt(val, 10);
      this._syncInput();
    }.bind(this));
    combobox.appendChild(minuteSelect);

    if (this.use12Hours) {
      const ampmSelect = document.createElement('div');
      ampmSelect.className = 'ant-time-picker-panel-select';
      ampmSelect.setAttribute('data-is-ampm', 'true');
      const ul = document.createElement('ul');
      (this._state.isPM ? ['PM', 'AM'] : ['AM', 'PM']).forEach(function (label) {
        const isPM = (label === 'PM');
        const li = document.createElement('li');
        li.textContent = label;
        li.setAttribute('data-ampm', isPM ? 'pm' : 'am');
        if (this._state.isPM === isPM) {
          li.classList.add('ant-time-picker-panel-cell-selected');
          li.classList.add('ant-time-picker-panel-select-option-selected');
        }
        li.addEventListener('click', function () {
          this._state.isPM = isPM;
          this._state.hours24 = isPM
            ? (this._state.hours12 === 12 ? 12 : this._state.hours12 + 12)
            : (this._state.hours12 === 12 ? 0 : this._state.hours12);
          ampmSelect.querySelectorAll('li').forEach(function (node) {
            node.classList.remove('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
          });
          li.classList.add('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
          if (!li.nextElementSibling) {
            ul.insertBefore(li, ul.firstChild);
          }
          this._syncInput();
        }.bind(this));
        ul.appendChild(li);
      }.bind(this));
      ampmSelect.appendChild(ul);
      combobox.appendChild(ampmSelect);
    }

    inner.appendChild(combobox);

    const addon = document.createElement('div');
    addon.className = 'ant-time-picker-panel-addon';
    if (this.showNowButton) {
      const nowLink = document.createElement('span');
      nowLink.className = 'ts-time-picker-now';
      nowLink.textContent = 'Now';
      nowLink.addEventListener('click', function () {
        const now = new Date();
        const minutes = Math.min(59, now.getMinutes());
        this._state.hours24 = now.getHours();
        this._state.hours12 = now.getHours() % 12 || 12;
        this._state.minutes = minutes;
        this._state.isPM = now.getHours() >= 12;
        this._syncInput();
        this._closePanel();
        this.onChange(this.getValue());
      }.bind(this));
      addon.appendChild(nowLink);
    }
    if (this.showOkayButton) {
      const okLink = document.createElement('span');
      okLink.className = 'ts-time-picker-ok';
      okLink.textContent = 'Okay';
      okLink.addEventListener('click', function (e) {
        e.stopPropagation();
        this._closePanel();
        this.onChange(this.getValue());
      }.bind(this));
      addon.appendChild(okLink);
    }
    if (addon.childNodes.length > 0) {
      inner.appendChild(addon);
    }

    panel.appendChild(inner);
    return panel;
  };

  TsTimePicker_standAlone.prototype._createSelect = function (type, options, selectedValue, onSelect) {
    const self = this;
    const wrap = document.createElement('div');
    wrap.className = 'ant-time-picker-panel-select';
    wrap.setAttribute('data-type', type);
    const ul = document.createElement('ul');
    options.forEach(function (opt) {
      const displayText = type === 'minute'
        ? String(opt).padStart(2, '0')
        : (type === 'hour' && !self.use12Hours ? String(opt).padStart(2, '0') : String(opt));
      const dataVal = type === 'minute' ? String(opt) : String(opt);
      const li = document.createElement('li');
      li.textContent = displayText;
      li.setAttribute('data-value', dataVal);
      var current = self.use12Hours ? self._state.hours12 : self._state.hours24;
      if (type === 'minute') {
        current = Math.floor(self._state.minutes / 10) * 10;
      }
      const isSelected = opt === current;
      if (isSelected) {
        li.classList.add('ant-time-picker-panel-cell-selected');
        li.classList.add('ant-time-picker-panel-select-option-selected');
      }
      li.addEventListener('click', function () {
        wrap.querySelectorAll('li').forEach(function (n) {
          n.classList.remove('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
        });
        li.classList.add('ant-time-picker-panel-cell-selected', 'ant-time-picker-panel-select-option-selected');
        if (!wrap.hasAttribute('data-is-ampm')) {
          self._suppressPanelScrollSync = true;
          scrollSelectedToTopOfList(wrap, li);
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              self._suppressPanelScrollSync = false;
            });
          });
        }
        onSelect(li.getAttribute('data-value'));
      });
      ul.appendChild(li);
    }.bind(this));
    wrap.appendChild(ul);
    return wrap;
  };

  TsTimePicker_standAlone.prototype._handleTypedInput = function () {
    if (!this._input) return;
    var cleaned = sanitizeTypedTimeInput(this._input.value);
    if (cleaned !== this._input.value) {
      this._input.value = cleaned;
    }
  };

  TsTimePicker_standAlone.prototype._handleTypedKeyDown = function (e) {
    if (this.disabled) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      this._commitTypedInput();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length !== 1) return;
    if (!/[0-9:APMapm]/.test(e.key)) {
      e.preventDefault();
    }
  };

  TsTimePicker_standAlone.prototype._commitTypedInput = function () {
    if (!this._input) return;
    var cleaned = sanitizeTypedTimeInput(this._input.value);
    if (cleaned !== this._input.value) {
      this._input.value = cleaned;
    }

    var parsed = parseTypedTimeInput(cleaned, this.use12Hours, this.minuteStep, this._state);
    if (!parsed) {
      this._syncInput();
      return;
    }

    var previousValue = this.getValue();
    this._state.hours24 = parsed.hours24;
    this._state.hours12 = parsed.hours12;
    this._state.minutes = parsed.minutes;
    this._state.isPM = parsed.isPM;
    this._syncInput();

    if (this.getValue() !== previousValue) {
      this.onChange(this.getValue());
    }
  };

  TsTimePicker_standAlone.prototype._syncInput = function () {
    if (!this._input) return;
    this._input.value = this.use12Hours
      ? format12h(this._state.hours24, this._state.minutes)
      : toHHmm(this._state.hours24, this._state.minutes);
  };

  function scrollSelectedToTopOfList(container, listItem) {
    if (!container || !listItem) return;
    function applyScroll() {
      var cRect = container.getBoundingClientRect();
      var iRect = listItem.getBoundingClientRect();
      var nextTop = container.scrollTop + (iRect.top - cRect.top);
      var maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
      container.scrollTop = Math.max(0, Math.min(maxScroll, nextTop));
    }
    applyScroll();
    requestAnimationFrame(applyScroll);
  }

  function findVerticalScrollableWithin(start, boundary) {
    var el = start;
    while (el && el !== boundary) {
      if (el.nodeType !== 1) {
        el = el.parentElement;
        continue;
      }
      var style = window.getComputedStyle(el);
      var oy = style.overflowY;
      if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && el.scrollHeight > el.clientHeight) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  TsTimePicker_standAlone.prototype._scrollToSelected = function () {
    if (!this._panel) return;
    var picker = this;
    picker._suppressPanelScrollSync = true;
    try {
      picker._panel.querySelectorAll('.ant-time-picker-panel-select[data-type="hour"], .ant-time-picker-panel-select[data-type="minute"]').forEach(function (scrollContainer) {
        if (scrollContainer.hasAttribute('data-is-ampm')) return;
        var el = scrollContainer.querySelector('li.ant-time-picker-panel-cell-selected');
        if (!el) return;
        scrollSelectedToTopOfList(scrollContainer, el);
      });
    } finally {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          picker._suppressPanelScrollSync = false;
        });
      });
    }
  };

  TsTimePicker_standAlone.prototype._attachHoursScrollLog = function () {
    if (!this._panel) return;
    var panel = this._panel;
    var onWheel = function (e) {
      if (!panel.contains(e.target)) return;
      var scrollable = findVerticalScrollableWithin(e.target, panel);
      if (!scrollable) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      var maxScroll = Math.max(0, scrollable.scrollHeight - scrollable.clientHeight);
      var nextTop = scrollable.scrollTop + e.deltaY;
      if (nextTop < 0) {
        e.preventDefault();
        e.stopPropagation();
        scrollable.scrollTop = 0;
        return;
      }
      if (nextTop > maxScroll) {
        e.preventDefault();
        e.stopPropagation();
        scrollable.scrollTop = maxScroll;
        return;
      }
    };
    panel.addEventListener('wheel', onWheel, { passive: false, capture: true });
    this._hoursScrollLogCleanup = function () {
      panel.removeEventListener('wheel', onWheel, { capture: true });
      this._hoursScrollLogCleanup = null;
    }.bind(this);
  };

  TsTimePicker_standAlone.prototype.getValue = function () {
    return toHHmm(this._state.hours24, this._state.minutes);
  };

  TsTimePicker_standAlone.prototype.setValue = function (value) {
    this._state = parseTime(value, this.minuteStep);
    this._syncInput();
  };

  TsTimePicker_standAlone.prototype.setDisabled = function (disabled) {
    this.disabled = !!disabled;
    if (this._input) this._input.disabled = this.disabled;
  };

  /**
   * Close the panel programmatically without firing onChange.
   */
  TsTimePicker_standAlone.prototype.close = function () {
    this._closePanel();
  };

  TsTimePicker_standAlone.prototype.destroy = function () {
    this._closePanel();
    if (this._wrapper && this._wrapper.parentNode) {
      this._wrapper.parentNode.removeChild(this._wrapper);
    }
    this._wrapper = null;
    this._input = null;
    this._panel = null;
  };

  if (typeof global !== 'undefined') {
    global.TsTimePicker_standAlone = TsTimePicker_standAlone;
  }
  return TsTimePicker_standAlone;
})(typeof window !== 'undefined' ? window : this);

if (typeof module !== 'undefined' && module.exports) {
  module.exports = TsTimePickerExport;
  module.exports.default = TsTimePickerExport;
}
